import type { ContentTypeName } from "./core"

export type ImageTransformFit = "cover" | "contain" | "fill" | "inside" | "outside"

export type ImageTransformPreset =
  | "newsCard"
  | "newsDetail"
  | "careerCard"
  | "workCover"
  | "workModal"
  | "logo"
  | "markdown"

export type ImageTransformAsset = {
  objectKey?: string
  width?: number
  height?: number
}

export type ImageTransformOptions = {
  bucket?: string
  endpoint?: string
  stage?: string
}

export type AssetReference = {
  objectKey: string
  width?: number
  height?: number
  mimeType?: string
}

export type ImagePrewarmFetcher = (
  url: string,
  init: {
    method: "HEAD" | "GET"
    signal?: AbortSignal
  }
) => Promise<{
  ok: boolean
  status: number
}>

export type ImagePrewarmReport = {
  attempted: number
  succeeded: number
  failed: number
  skipped: number
  durationMs: number
  failures: Array<{
    url: string
    status?: number
    error?: string
  }>
}

export type ImagePrewarmOptions = {
  concurrency?: number
  fetcher?: ImagePrewarmFetcher
  method?: "HEAD" | "GET"
  timeoutMs?: number
}

export type ImagePrewarmUrlOptions = ImageTransformOptions & {
  includeSrcSetScales?: boolean
}

export type ImageTransformFormat = "webp" | "jpeg" | "png"

export type ImageTransformPresetConfig = {
  width: number
  height?: number
  fit: ImageTransformFit
  format?: ImageTransformFormat
  quality?: number
  sizes: string
}

export const imageTransformPresets: Record<ImageTransformPreset, ImageTransformPresetConfig> = {
  careerCard: {
    width: 640,
    height: 750,
    fit: "cover",
    format: "webp",
    quality: 80,
    sizes: "(min-width: 900px) 33vw, 100vw",
  },
  logo: {
    width: 360,
    fit: "inside",
    sizes: "360px",
  },
  markdown: {
    width: 1200,
    fit: "inside",
    format: "webp",
    quality: 80,
    sizes: "(min-width: 1200px) 1200px, 100vw",
  },
  newsCard: {
    width: 720,
    height: 405,
    fit: "cover",
    format: "webp",
    quality: 80,
    sizes: "(min-width: 900px) 33vw, 100vw",
  },
  newsDetail: {
    width: 1440,
    height: 810,
    fit: "cover",
    format: "webp",
    quality: 80,
    sizes: "(min-width: 1440px) 1440px, 100vw",
  },
  workCover: {
    width: 720,
    height: 405,
    fit: "cover",
    format: "webp",
    quality: 78,
    sizes: "(min-width: 900px) 50vw, 100vw",
  },
  workModal: {
    width: 960,
    fit: "inside",
    format: "webp",
    quality: 78,
    sizes: "(min-width: 960px) 960px, 100vw",
  },
}

export const imagePrewarmPresetsByContentType: Partial<Record<ContentTypeName, ImageTransformPreset[]>> = {
  careers: ["careerCard", "markdown"],
  companyHistory: ["logo", "markdown"],
  news: ["newsCard", "newsDetail", "markdown"],
  workMarketing: ["workCover", "workModal"],
  workProducts: ["workCover"],
}

const imageResizerEndpoints: Record<string, string> = {
  dev: "https://dev.resizer.playbrain.com",
  local: "https://dev.resizer.playbrain.com",
}

const imageResizerBuckets: Record<string, string> = {
  dev: "",
  local: "",
  staging: "playbrain-website-2024-staging-content-assets",
  prod: "playbrain-website-2024-prod-content-assets",
}

const supportedAssetPrefixes = ["content-assets/", "dashboard-assets/", "sanity-assets/"]
const rawAssetUrlPattern =
  /https?:\/\/(?:(?:dev|staging)\.asset|asset|dev\.assets|assets(?:\.(?:dev|staging))?)\.playbrain\.com\/((?:content-assets|dashboard-assets|sanity-assets)\/[^"'<>\s)]+)/g

export function imagePrewarmPresetsForContentType(type: ContentTypeName): ImageTransformPreset[] {
  return imagePrewarmPresetsByContentType[type] ?? []
}

export function imageTransformEndpoint(stage = "dev") {
  if (!stage || stage === "local" || stage === "dev") return imageResizerEndpoints.dev
  return imageResizerEndpoints[stage] ?? ""
}

export function imageTransformBucket(stage = "dev") {
  return imageResizerBuckets[stage] ?? imageResizerBuckets.dev
}

export function imageTransformSizes(preset: ImageTransformPreset) {
  return imageTransformPresets[preset].sizes
}

export function resizedAssetUrl(
  asset: ImageTransformAsset | null | undefined,
  preset: ImageTransformPreset = "markdown",
  options: ImageTransformOptions & { scale?: number } = {}
) {
  const objectKey = assetObjectKeyFromUrl(asset?.objectKey)
  if (!objectKey) return null

  const stage = options.stage || "dev"
  const endpoint = (options.endpoint || imageTransformEndpoint(stage)).replace(/\/$/, "")
  const bucket = options.bucket || imageTransformBucket(stage)
  if (!endpoint || !bucket) return null

  const request = {
    bucket,
    key: objectKey,
    edits: {
      resize: resizeRequest(asset, preset, options.scale ?? 1),
      ...formatRequest(imageTransformPresets[preset]),
    },
  }

  return `${endpoint}/${base64Encode(JSON.stringify(request))}`
}

export function resizedAssetUrlFromValue(
  value: string | null | undefined,
  preset: ImageTransformPreset = "markdown",
  options: ImageTransformOptions & { scale?: number } = {}
) {
  const objectKey = assetObjectKeyFromUrl(value)
  return objectKey ? resizedAssetUrl({ objectKey }, preset, options) : null
}

export function resizedAssetSrcSet(
  asset: ImageTransformAsset | null | undefined,
  preset: ImageTransformPreset = "markdown",
  options: ImageTransformOptions = {}
) {
  const entries = [1, 2]
    .map((scale) => {
      const url = resizedAssetUrl(asset, preset, { ...options, scale })
      return url ? `${url} ${scale}x` : null
    })
    .filter(Boolean)

  return [...new Set(entries)].join(", ") || undefined
}

export function imageTransformRuntimeOptions(env: Record<string, string | undefined>) {
  return {
    bucket: env.PUBLIC_IMAGE_RESIZER_BUCKET || env.VITE_PUBLIC_IMAGE_RESIZER_BUCKET || env.ASSET_BUCKET_NAME,
    endpoint: env.PUBLIC_IMAGE_RESIZER_ENDPOINT || env.VITE_PUBLIC_IMAGE_RESIZER_ENDPOINT,
    stage: env.PUBLIC_STAGE || env.VITE_PUBLIC_STAGE,
  }
}

export function collectAssetKeysFromRecord(recordValues: Record<string, unknown>): AssetReference[] {
  const references = new Map<string, AssetReference>()

  function addReference(objectKey: string, source?: Record<string, unknown>) {
    const normalizedKey = normalizeAssetObjectKey(objectKey)
    if (!normalizedKey) return

    const existing = references.get(normalizedKey)
    references.set(normalizedKey, {
      objectKey: normalizedKey,
      width: existing?.width ?? numberField(source, "width"),
      height: existing?.height ?? numberField(source, "height"),
      mimeType: existing?.mimeType ?? stringField(source, "mimeType"),
    })
  }

  function visit(value: unknown) {
    if (typeof value === "string") {
      for (const objectKey of assetObjectKeysFromString(value)) addReference(objectKey)
      return
    }

    if (Array.isArray(value)) {
      for (const item of value) visit(item)
      return
    }

    if (!value || typeof value !== "object") return

    const record = value as Record<string, unknown>
    const directObjectKey = stringField(record, "objectKey") ?? stringField(record, "s3Key")
    if (directObjectKey) addReference(directObjectKey, record)

    for (const urlField of ["url", "s3Url", "cdnUrl"]) {
      const url = stringField(record, urlField)
      if (!url) continue
      for (const objectKey of assetObjectKeysFromString(url)) addReference(objectKey, record)
    }

    for (const child of Object.values(record)) visit(child)
  }

  visit(recordValues)
  return [...references.values()]
}

export function imagePrewarmUrls(
  asset: ImageTransformAsset,
  presets: ImageTransformPreset[],
  options: ImagePrewarmUrlOptions = {}
): string[] {
  const urls = new Set<string>()
  const includeSrcSetScales = options.includeSrcSetScales ?? true

  for (const preset of presets) {
    const url = resizedAssetUrl(asset, preset, options)
    if (url) urls.add(url)

    if (!includeSrcSetScales) continue
    const srcSet = resizedAssetSrcSet(asset, preset, options)
    if (!srcSet) continue

    for (const candidate of srcSet.split(",")) {
      const candidateUrl = candidate.trim().split(/\s+/)[0]
      if (candidateUrl) urls.add(candidateUrl)
    }
  }

  return [...urls]
}

export async function prewarmImageTransforms(
  urls: string[],
  options: ImagePrewarmOptions = {}
): Promise<ImagePrewarmReport> {
  const startedAt = nowMs()
  const uniqueUrls = [...new Set(urls.filter((url) => typeof url === "string" && url.length > 0))]
  const fetcher = options.fetcher ?? defaultPrewarmFetcher()
  const report: ImagePrewarmReport = {
    attempted: 0,
    succeeded: 0,
    failed: 0,
    skipped: 0,
    durationMs: 0,
    failures: [],
  }

  if (uniqueUrls.length === 0) {
    report.durationMs = elapsedMs(startedAt)
    return report
  }

  if (!fetcher) {
    report.skipped = uniqueUrls.length
    report.durationMs = elapsedMs(startedAt)
    return report
  }

  const activeFetcher = fetcher
  const concurrency = positiveInteger(options.concurrency, 4)
  const method = options.method ?? "HEAD"
  const timeoutMs = positiveInteger(options.timeoutMs, 5000)
  let nextIndex = 0

  async function worker() {
    while (nextIndex < uniqueUrls.length) {
      const url = uniqueUrls[nextIndex]
      nextIndex += 1
      if (!url) continue
      report.attempted += 1

      try {
        const response = await fetchWithTimeout(activeFetcher, url, method, timeoutMs)
        if (response.ok) {
          report.succeeded += 1
        } else {
          report.failed += 1
          report.failures.push({ url, status: response.status })
        }
      } catch (error) {
        report.failed += 1
        report.failures.push({
          url,
          error: error instanceof Error ? error.message : String(error),
        })
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(concurrency, uniqueUrls.length) }, () => worker()))
  report.durationMs = elapsedMs(startedAt)
  return report
}

export function rewriteAssetImageUrlsInHtml(
  html: string,
  preset: ImageTransformPreset = "markdown",
  options: ImageTransformOptions = {}
) {
  return html.replace(
    /(src=["'])(https?:\/\/(?:(?:dev|staging)\.asset|asset|dev\.assets|assets(?:\.(?:dev|staging))?)\.playbrain\.com\/((?:content-assets|dashboard-assets|sanity-assets)\/[^"'<>\s]+))(["'])/g,
    (match, prefix: string, _url: string, objectKey: string, suffix: string) => {
      const transformed = resizedAssetUrl({ objectKey: decodeURIComponent(objectKey) }, preset, options)
      return transformed ? `${prefix}${transformed}${suffix}` : match
    }
  )
}

export function assetObjectKeyFromUrl(value: string | null | undefined): string | undefined {
  if (!value?.trim()) return undefined
  const trimmed = value.trim()
  const directKey = normalizeAssetObjectKey(trimmed)
  if (directKey) return directKey

  let url: URL
  try {
    url = new URL(trimmed)
  } catch {
    return undefined
  }

  if (!isSupportedRawAssetHost(url.hostname)) return undefined
  return normalizeAssetObjectKey(url.pathname)
}

function assetObjectKeysFromString(value: string): string[] {
  const keys = new Set<string>()

  for (const match of value.matchAll(rawAssetUrlPattern)) {
    const objectKey = normalizeAssetObjectKey(match[1] ?? "")
    if (objectKey) keys.add(objectKey)
  }

  const directKey = normalizeAssetObjectKey(value)
  if (directKey) keys.add(directKey)

  return [...keys]
}

function normalizeAssetObjectKey(value: string) {
  const decoded = safeDecodeURIComponent(value)
  const objectKey = decoded.split("#")[0]?.split("?")[0]?.replace(/^\/+/, "") ?? ""
  if (!supportedAssetPrefixes.some((prefix) => objectKey.startsWith(prefix))) return undefined
  return objectKey
}

function isSupportedRawAssetHost(hostname: string) {
  return /^(?:(?:dev|staging)\.asset|asset|dev\.assets|assets(?:\.(?:dev|staging))?)\.playbrain\.com$/i.test(
    hostname
  )
}

function stringField(record: Record<string, unknown> | undefined, key: string) {
  const value = record?.[key]
  return typeof value === "string" && value.trim().length > 0 ? value : undefined
}

function numberField(record: Record<string, unknown> | undefined, key: string) {
  const value = record?.[key]
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : undefined
}

function positiveInteger(value: number | undefined, fallback: number) {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : fallback
}

function defaultPrewarmFetcher(): ImagePrewarmFetcher | undefined {
  if (typeof fetch !== "function") return undefined
  return (url, init) => fetch(url, init)
}

async function fetchWithTimeout(
  fetcher: ImagePrewarmFetcher,
  url: string,
  method: "HEAD" | "GET",
  timeoutMs: number
) {
  if (typeof AbortController !== "function") return fetcher(url, { method })

  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), timeoutMs)

  try {
    return await fetcher(url, {
      method,
      signal: controller.signal,
    })
  } finally {
    clearTimeout(timeout)
  }
}

function nowMs() {
  return typeof performance === "object" && typeof performance.now === "function" ? performance.now() : 0
}

function elapsedMs(startedAt: number) {
  const current = nowMs()
  return Math.max(0, Math.round(current - startedAt))
}

function safeDecodeURIComponent(value: string) {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function resizeRequest(
  asset: ImageTransformAsset | null | undefined,
  preset: ImageTransformPreset,
  scale: number
) {
  const config = imageTransformPresets[preset]
  return {
    width: scaledDimension(config.width, asset?.width, scale),
    ...(config.height ? { height: scaledDimension(config.height, asset?.height, scale) } : {}),
    fit: config.fit,
  }
}

function formatRequest(config: ImageTransformPresetConfig) {
  if (!config.format) return {}

  return {
    toFormat: config.format,
    ...(config.quality ? { [config.format]: { quality: config.quality } } : {}),
  }
}

function scaledDimension(target: number, source: number | undefined, scale: number) {
  const scaled = Math.max(1, Math.round(target * scale))
  return typeof source === "number" && Number.isFinite(source) && source > 0
    ? Math.min(scaled, Math.round(source))
    : scaled
}

function base64Encode(value: string) {
  const bytes = new TextEncoder().encode(value)
  let binary = ""
  for (const byte of bytes) binary += String.fromCharCode(byte)
  if (typeof btoa === "function") return btoa(binary)

  const buffer = (
    globalThis as unknown as {
      Buffer?: { from: (input: string, encoding: string) => { toString: (encoding: string) => string } }
    }
  ).Buffer
  if (buffer) return buffer.from(binary, "binary").toString("base64")
  throw new Error("No base64 encoder is available.")
}
